import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router-dom';
import Avatar from '../../../assets/images/user/Avatar.png';
import AvatarDark from '../../../assets/images/user/AvatarDark.png';
import { getDeclinationValue } from '../../../utils';
import { useUserAddress } from '../../../hooks/useUserAddress';
import { useAppSelector } from '../../../hooks/redux';
import { bonusesDeclinationRU, bonusesDeclinationUA } from '../../../constants/configuration';
import { AccountSettingRouteNames, GlobalRouteNames, ScreenSize } from '../../../models/Common';
import { BasicButton } from '../../commons/buttons/BasicButton/BasicButton';
import { CustomSkeletonElement } from '../../commons/CustomSkeletonElement';
import { CustomNumberFormat } from '../../commons/CustomNumberFormat';

interface UserInfoBlockProps {
  width: number;
}

export const UserInfoBlock: FC<UserInfoBlockProps> = ({ width }) => {
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { userInfoState, isUserInfoDownloaded } = useAppSelector((state) => state.userInfoReducer);
  const { theme } = useAppSelector((state) => state.appReducer);
  const { userAddress } = useUserAddress(userInfoState, selectedLanguage);
  const isTablet = useMediaQuery('(max-width:1279px)');
  const { t } = useTranslation();
  const history = useHistory();
  const userName =
    isUserInfoDownloaded && userInfoState.Name.split(' ').length > 1
      ? `${userInfoState.Name.split(' ')[1]}`
      : userInfoState.Name;

  const bonusesSuffix = isUserInfoDownloaded
    ? `${getDeclinationValue(
        userInfoState.Bonus,
        selectedLanguage,
        bonusesDeclinationUA,
        bonusesDeclinationRU,
        'bonuses',
      )}`
    : undefined;

  const navigateToAccountSettings = () => {
    history.push(`${GlobalRouteNames.AccountSettingPage}${AccountSettingRouteNames.AccountPage}`);
  };

  return isUserInfoDownloaded ? (
    <BasicButton
      className={`user-info-block ${width < ScreenSize.Desktop ? 'card card__primary tablet' : ''}`}
      handleClick={navigateToAccountSettings}
    >
      {!isTablet && (
        <div className="user-info-block__avatar">
          <img src={theme === 'light' ? Avatar : AvatarDark} alt="avatar" />
        </div>
      )}
      <div className="user-info-block__content">
        <div className="user-info-block__content__name">
          <span style={{ fontWeight: 500 }}>{userName}</span>
          {isUserInfoDownloaded && isTablet ? (
            <>
              <hr />
              <span style={{ color: 'var(--color-gray)' }}>{t('common.contract')}</span> <span>{userInfoState.id}</span>
            </>
          ) : null}
        </div>
        {isUserInfoDownloaded && !isTablet && (
          <div className="user-info-block__content__contract">
            <span style={{ color: 'var(--color-gray)' }}>{t('common.contract')}</span> <span>{userInfoState.id}</span>
          </div>
        )}
        {userAddress && isTablet ? (
          <div className="user-info-block__content__address">
            <span>{userAddress}</span>
          </div>
        ) : null}
        <div className="user-info-block__content__balance">
          {isUserInfoDownloaded &&
            (Number.isInteger(userInfoState.Balance) ? (
              <span>
                {userInfoState.Balance} {t('common.uah')}
              </span>
            ) : (
              <CustomNumberFormat value={userInfoState.Balance} suffix={t('common.uah')} />
            ))}
          {isUserInfoDownloaded && userInfoState.Permissions?.Info?.ShowBonuses ? (
            <>
              <hr />
              {Number.isInteger(userInfoState.Bonus) ? (
                <span>
                  {userInfoState.Bonus} {bonusesSuffix}
                </span>
              ) : (
                <CustomNumberFormat value={userInfoState.Bonus} suffix={bonusesSuffix} />
              )}
            </>
          ) : null}
        </div>
      </div>
    </BasicButton>
  ) : (
    <CustomSkeletonElement skeletonContainerClassName="skeleton-container skeleton" />
  );
};
